import type { AccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import type { DeepPartial } from '@frameio/utils';
import type { AvatarSizes } from '@frameio/vapor';
import type { AvatarsContentFragment } from '../components/NotificationCell/Avatars/gql/generated/Avatars';

const SINGLE_AVATAR_SIZE = 24;
const MULTIPLE_AVATARS_SIZE = 16;

/**
 * Determines the appropriate avatar size based on the number of avatars.
 *
 * This function returns a size based on the number of avatars provided. If two or more avatars
 * are present, it will return a smaller size suitable for displaying multiple avatars.
 * Otherwise, it will return a larger size for a single avatar.
 *
 * @param {DeepPartial<AvatarsContentFragment['avatars'][number]> | undefined} avatars - An array of `NotificationAvatar` objects. The number of
 * avatars in this array determines the returned avatar size. If `avatars` is undefined or empty,
 * the size will default to that of a single avatar.
 *
 * @returns {AvatarSizes} The avatar size, which is either a size for a single avatar or a smaller
 * size for multiple avatars.
 *
 * @example
 * // Returns 24 (size for a single avatar)
 * getAvatarSize([{ id: 'avatar1' }]);
 *
 * @example
 * // Returns 16 (size for multiple avatars)
 * getAvatarSize([{ id: 'avatar1' }, { id: 'avatar2' }]);
 */
export const getAvatarSize = (
  avatars: (
    | DeepPartial<AvatarsContentFragment['avatars'][number]>
    | DeepPartial<AccessRequestNodeFragment['requesterUser']>
    | undefined
  )[]
): AvatarSizes => {
  const avatarCount = avatars?.length ?? 0;
  const size = avatarCount >= 2 ? MULTIPLE_AVATARS_SIZE : SINGLE_AVATAR_SIZE;
  return size;
};
